'use client'

import React, { useEffect, useState } from 'react'
import './UpsellPopUp.css'
import ServiceSectionDesktop from './ServiceSectionDesktop'
import ServiceSectionMobileB from './ServiceSectionMobileB'
import { useShippingPageContext } from '../../../v2/ShippingV2/providers/ShippingPageProvider'
import { useCartContext } from '../../../../customHooks/useCart'
import { translate } from '../../../../utils/translator'
import { useAppContext } from '../../../../providers/AppProvider'
import { MainTracker } from '../../../../PixelTrackers/MainTracker'

// Constants
const FLOWZ_TREATMENT_COST = 4.20;
const DEFAULT_FUNNEL_TREATMENT_COST = 18.90;
const CANNABIS_ID_COST = 6.90;
const DELIVERY_COST = 8;
const SMALL_PHONE_WIDTH = 380;

// Custom SVG icons
const XIcon = () => (
  <svg className="x-icon" fill="none" stroke="white" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
)

interface UpsellPopUpV2Props {
  onClose?: () => void
  onChoosePremium?: () => void
  onKeepPrescription?: () => void
  abTestVariant?: 'A' | 'B' | null
}

const UpsellPopUpV2: React.FC<UpsellPopUpV2Props> = ({
  onClose,
  onChoosePremium,
  onKeepPrescription,
  abTestVariant = null,
}) => {
  const [openedAt, setOpenedAt] = useState<number | null>(null);
  const [isSmallPhone, setIsSmallPhone] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallPhone(window.innerWidth < SMALL_PHONE_WIDTH);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const { setStickyNotification } = useAppContext();

  const {
    setShowUpsellPopUp,
    upsellProducts,
    handleShippingSubmit,
    currentFormData,
    setPreSelectedExternalPharmacy,
    showUpsellOnShippingPage,
    language,
    upgradePrescriptionOnlyToPremium,
    setWasUpgradedToPremium,
    setIsExpressPrescriptionReview,
    isOriginalCartFlowFlowzz,
    setExpressDeliveryState,
    setExpressStockCode,
    setUpsellPopupDismissed,
    handleDeliveryOptionChange,
    setCouponWidgetState,
  } = useShippingPageContext();

  const {
    getCart,
    saveCart
  } = useCartContext();

  const cart = getCart();
  const cartTreatmentFee = cart.treatment_fee || DEFAULT_FUNNEL_TREATMENT_COST;

  // Calculate prices (simplified without alternatives)
  const sumOfPremiumProducts = upsellProducts.reduce((total, product) => {
    if (product.product_in_stock && product.item_price) {
      return total + product.item_price;
    }
    return total;
  }, 0);

  const sumOfFlowzPrices = upsellProducts.reduce((total, product) => total + (product.flowz_price || 0), 0);
  const totalCost = sumOfFlowzPrices + (isOriginalCartFlowFlowzz ? FLOWZ_TREATMENT_COST : cartTreatmentFee) + CANNABIS_ID_COST + DELIVERY_COST;

  // Determine if premium is cheaper
  const isPremiumCheaper = sumOfPremiumProducts < totalCost;
  const savingsAmount = totalCost - sumOfPremiumProducts;

  const t = (key: string) => translate(language, "shipping", `UpsellPopUpV2/${key}`) as string;


  // A/B test tracking helpers
  const variant = abTestVariant || null;
  const priceScenario = isPremiumCheaper ? 'cheaper' : 'expensive';

  useEffect(() => {
    const now = Date.now();
    setOpenedAt(now);
    setUpsellPopupDismissed(true);
    MainTracker.track('pv', 'View Pop Up', { 'Pop Up Name': 'Upgrade Order Modal' });

    // Upsell_Popup_Shown event
    MainTracker.track('custom', 'Upsell_Popup_Shown', {
      variant,
      price_scenario: priceScenario,
    });

  }, []);

  const handleClose = (closeReason: 'x_button' | 'outside_click' = 'x_button') => {
    const timeToClose = openedAt ? (Date.now() - openedAt) : 0;
    
    // Legacy tracking
    window.eventsTracking.track({ e: "Upgrade Order Modal closed from x button", p: { timeSpentMs: timeToClose } });
    
    // New Mixpanel tracking - Upsell_Popup_Closed
    MainTracker.track('custom', 'Upsell_Popup_Closed', {
      variant,
      price_scenario: priceScenario,
      close_reason: closeReason,
      time_to_close_ms: timeToClose,
    });
    
    setShowUpsellPopUp(false);
    onClose?.();
  }

  const handlePremium = async () => {
    const timeToClick = openedAt ? (Date.now() - openedAt) : 0;
    
    // Legacy tracking
    window.eventsTracking.track({ 
      e: "Upgrade Order Modal accepted", 
      p: { timeSpentMs: timeToClick } 
    });

    MainTracker.track('custom', 'Upsell_CTA_Clicked', {
      variant,
      price_scenario: priceScenario,
      cta_label: 'upgrade order',
      time_to_cta_click_ms: timeToClick,
    });

    MainTracker.track('custom', 'upsale_accepted', {
      user_id: window.eventsTracking?.getData?.("customerID") || null,
      order_id: cart.id || null,
      upsell_type: 'premium',
      variant,
      price_scenario: priceScenario,
      savings_euros: Math.round(savingsAmount * 100) / 100,
    });

    // No alternative products, so just empty arrays
    upgradePrescriptionOnlyToPremium([], [])
      .then(async (response: any) => {
        const cartView = response.data.cartView as CartView;
        const isExpressFlow = response.data.isExpressFlow;
        
        // Update the cartView
        if (cartView) {
          saveCart({ ...cartView.cart, products: cartView.products, timekitBooking: cartView.timekitBooking });
          setExpressStockCode(cartView.expressStockCode);
        }

        // Clear stale coupon state so handleDeliveryOptionChange won't re-apply the old coupon
        setCouponWidgetState((current) => ({
          ...current,
          inputCode: "",
          errorMsg: "",
          exposeExtraEnterContainer: false,
        }));

        setExpressDeliveryState((prevState) => ({
          ...prevState,
          isAvailable: isExpressFlow,
          deliveryPriceLoading: isExpressFlow,
          isEnabled: false
        }));

        setPreSelectedExternalPharmacy(null);
        setIsExpressPrescriptionReview(false);
        setWasUpgradedToPremium(true);
        MainTracker.superProperties({ "Purchase Type": "Premium" });

        if (showUpsellOnShippingPage) {
          setShowUpsellPopUp(false);
        } else {
          // Call handleShippingSubmit with skipUpsell = true to bypass the upsell check
          // Pass wasUpgradedToPremium = true directly instead of relying on state
          handleShippingSubmit(currentFormData!, false, false, true, true);
        }
        await handleDeliveryOptionChange("regular");
      })
      .catch((error) => {
        setStickyNotification({
          show: true,
          level: "error",
          showInfoIcon: true,
          title: translate(language, "default", "ServerError/title") as string,
          content: translate(language, "default", "ServerError/info") as string,
          autoFade: true,
        });
      });

    onChoosePremium?.();
  }

  const handleKeepPrescription = async () => {
    const timeToClick = openedAt ? (Date.now() - openedAt) : 0;
    
    // Legacy tracking
    window.eventsTracking.track({ e: "Upgrade Order Modal rejected", p: { timeSpentMs: timeToClick } });
    
    MainTracker.track('custom', 'Upsell_CTA_Clicked', {
      variant,
      price_scenario: priceScenario,
      cta_label: 'keep prescription only',
      time_to_cta_click_ms: timeToClick,
    });
    
    if (showUpsellOnShippingPage) {
      setShowUpsellPopUp(false);
    } else {
      // Call handleShippingSubmit with skipUpsell = true to bypass the upsell check
      handleShippingSubmit(currentFormData!, false, false, true);
    }

    onKeepPrescription?.();
  }

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Close only if clicking the overlay itself, not the modal container
    if (e.target === e.currentTarget) {
      handleClose('outside_click');
    }
  };

  return (
    <div 
      id="upgrade-order-modal-overlay" 
      className="fixed inset-0 flex items-center justify-center bg-black/50 p-4 overflow-auto z-[101] upsell-v2-overlay-animate"
      onClick={handleOverlayClick}
    >
      {/* Main Container */}
      <div 
        id="upgrade-order-modal-container" 
        className="w-full max-w-[650px] max-h-[95dvh] rounded-2xl overflow-hidden bg-white shadow-2xl my-auto z-[999999999] upsell-v2-container-animate"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Mobile */}
        <div 
          className={`relative flex items-center justify-center md:hidden rounded-2xl upsell-v2-header-gradient ${isSmallPhone ? 'min-h-[44px] px-3 py-2 pr-10' : 'min-h-[50px] px-6 py-2.5 pr-12'}`}
        >
          <span className={`text-white font-semibold text-center font-roboto tracking-wider ${isSmallPhone ? 'text-[13px]' : 'text-[15px]'}`}>
            {isPremiumCheaper 
              ? t("header_cheaper") 
              : <>{t("header_expensive_line1")}<br/>{t("header_expensive_line2")}</>
            }
          </span>
          <button
            id="upgrade-order-modal-close"
            onClick={() => handleClose('x_button')}
            className={`absolute text-white p-1 transition-colors bg-transparent border-none outline-none ${isSmallPhone ? 'right-3' : 'right-4'}`}
            aria-label="Close dialog"
          >
            <XIcon />
          </button>
        </div>

        {/* Content */}
        <div 
          className={`bg-white overflow-y-auto ${isSmallPhone ? 'max-h-[calc(95dvh-44px)] px-4 py-4' : 'max-h-[calc(95dvh-50px)] px-7 py-6'}`}
        >
          {/* Desktop subtitle */}
          <p className="hidden md:block text-center font-semibold text-gray-900 mb-1 font-poppins text-[15px]">
            {isPremiumCheaper ? t("desktop_subtitle_cheaper") : t("desktop_subtitle_expensive")}
          </p> 
          
          {/* Title - Mobile */}
          <h1 
            className={`md:hidden text-center font-semibold mb-1 upsell-v2-title-gradient font-poppins leading-tight ${isSmallPhone ? 'text-[24px]' : 'text-[26px]'}`}
          >
            {isPremiumCheaper && t("title")}
          </h1>

          {/* Title - Desktop */}
          <h1 
            className="hidden md:block text-center font-semibold mb-1 upsell-v2-title-gradient font-poppins text-[36px] leading-tight"
          >
            {t("title")}
          </h1>

          {/* Desktop description */}
          <p className="hidden md:block text-center text-black mb-4 font-poppins text-base italic">
            {t("desktop_description_line1")}<br/>
            <span className="font-bold">{t("desktop_description_line2_bold")}</span> {t("desktop_description_line2")}
          </p>

          {/* Mobile description */}
          <p className={`md:hidden text-center text-black mb-3 font-poppins italic ${isSmallPhone ? 'text-[13px]' : 'text-[15px]'}`}>
            {t("mobile_description_line1")}<br/>
            <span className="font-bold">{t("mobile_description_line2_bold")}</span> {t("mobile_description_line2")}
          </p>

          {/* Service Cards - Mobile */}
          <div className="md:hidden">
            <ServiceSectionMobileB 
              premiumPrice={sumOfPremiumProducts} 
              regularPrice={totalCost}
              savingsAmount={savingsAmount}
              isPremiumCheaper={isPremiumCheaper}
              language={language}
            />
          </div>

          {/* Service Cards - Desktop */}
          <div className="hidden md:block">
            <ServiceSectionDesktop 
              premiumPrice={sumOfPremiumProducts} 
              regularPrice={totalCost}
              savingsAmount={savingsAmount}
              isPremiumCheaper={isPremiumCheaper}
              language={language}
            />
          </div>

          {/* Action Buttons */}
          <div 
            id="upgrade-order-modal-buttons" 
            className={`flex flex-col items-center mx-auto ${isSmallPhone ? 'gap-2' : 'gap-2.5'}`}
          >
            <button
              id="choose-premium-button"
              onClick={handlePremium}
              className={`w-full text-white font-semibold rounded-[10px] transition-all duration-200 focus:outline-none hover:scale-[1.02] active:scale-[0.98] cursor-pointer border-none upsell-v2-premium-btn ${isSmallPhone ? 'py-3 px-4 text-[13px]' : 'py-3.5 px-6 text-[15px]'}`}
            >
              {isPremiumCheaper 
                ? (t("cta_upgrade_cheaper") as string).replace('{{savings}}', savingsAmount.toFixed(0))
                : t("cta_upgrade_expensive")
              }
            </button>
            <button
              id="keep-prescription-button"
              onClick={handleKeepPrescription}
              className={`bg-transparent border-0 outline-0 cursor-pointer transition-colors hover:text-gray-900 underline font-roboto font-medium py-1 text-[#5B5B5B] ${isSmallPhone ? 'text-xs' : 'text-sm'}`}
            >
              {t("cta_keep_prescription")}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UpsellPopUpV2;
